import { Controller } from '@hotwired/stimulus'
import { setBodyOverflow, unsetBodyOverflow } from '../../../utils/dom'
import { debounce } from '../../../utils/functions'

export default class extends Controller {
    static targets = ['toggle', 'mainMenu', 'sticky', 'profileDropdown']

    declare readonly hasToggleTarget: boolean
    declare readonly toggleTarget: HTMLButtonElement
    declare readonly mainMenuTarget: HTMLDivElement
    declare readonly hasMainMenuTarget: boolean
    declare readonly stickyTarget: HTMLDivElement
    declare readonly hasStickyTarget: boolean
    declare readonly profileDropdownTarget: HTMLDivElement
    declare readonly hasProfileDropdownTarget: boolean

    private handleScroll?: () => void
    private handleResize?: () => void
    private isAnimating = false

    connect() {
        this.handleScroll = debounce(() => this.observeSticky(), 5)

        if (this.hasProfileDropdownTarget) {
            this.handleResize = debounce(() => this.positionDropdown(), 100)
            this.positionDropdown()
            window.addEventListener('resize', this.handleResize)
        }

        if (this.hasToggleTarget) {
            this.toggleTarget.addEventListener('click', this.handleClickToggle)
        }

        this.observeSticky()
        document.addEventListener('scroll', this.handleScroll)
    }

    disconnect() {
        if (this.handleScroll) {
            document.removeEventListener('scroll', this.handleScroll)
        }

        if (this.handleResize) {
            window.removeEventListener('resize', this.handleResize)
        }

        if (this.hasToggleTarget) {
            this.toggleTarget.removeEventListener('click', this.handleClickToggle)
        }
    }

    toggleMobileMenu() {
        if (!this.hasMainMenuTarget || !this.hasToggleTarget || this.isAnimating) {
            return
        }

        const isHidden = this.mainMenuTarget.classList.contains('hidden')

        if (isHidden) {
            this.openMobileMenu()
        } else {
            this.closeMobileMenu()
        }
    }

    openMobileMenu() {
        if (!this.hasMainMenuTarget) {
            return
        }

        const headerHeight = this.stickyTarget?.offsetHeight || 64
        const targetHeight = window.innerHeight - headerHeight

        this.isAnimating = true

        this.mainMenuTarget.classList.remove('hidden')
        this.mainMenuTarget.classList.add('animating')
        this.mainMenuTarget.style.height = '0px'
        this.toggleTarget.classList.add('opened')

        requestAnimationFrame(() => {
            this.animateHeight(0, targetHeight, () => {
                this.mainMenuTarget.style.height = ''
                this.mainMenuTarget.classList.remove('animating')
                this.mainMenuTarget.classList.add('opened')
                this.isAnimating = false
            })

            setBodyOverflow()
        })
    }

    closeMobileMenu() {
        if (!this.hasMainMenuTarget) {
            return
        }

        this.isAnimating = true
        const currentHeight = this.mainMenuTarget.scrollHeight
        this.mainMenuTarget.style.height = `${currentHeight}px`
        this.mainMenuTarget.classList.add('animating')
        this.toggleTarget.classList.remove('opened')

        requestAnimationFrame(() => {
            this.animateHeight(currentHeight, 0, () => {
                this.mainMenuTarget.classList.add('hidden')
                this.mainMenuTarget.style.height = ''
                this.mainMenuTarget.classList.remove('animating')
                this.mainMenuTarget.classList.remove('opened')

                unsetBodyOverflow()
                this.isAnimating = false
            })
        })
    }

    private animateHeight(startHeight: number, endHeight: number, onComplete: () => void) {
        const duration = 300
        const startTime = performance.now()

        const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime
            const progress = Math.min(elapsed / duration, 1)

            const easeInOutCubic = (t: number) => {
                return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
            }

            const easedProgress = easeInOutCubic(progress)
            const currentHeight = startHeight + (endHeight - startHeight) * easedProgress

            this.mainMenuTarget.style.height = `${currentHeight}px`

            if (progress < 1) {
                requestAnimationFrame(animate)
            } else {
                onComplete()
            }
        }

        requestAnimationFrame(animate)
    }

    private handleClickToggle = (event: Event) => {
        event.preventDefault()
        event.stopPropagation()

        this.toggleMobileMenu()
    }

    private observeSticky() {
        if (!this.hasStickyTarget) {
            return
        }

        const distanceTop = this.stickyTarget.getBoundingClientRect().top
        this.stickyTarget.classList.toggle('pinned', distanceTop <= 0)
    }

    private positionDropdown() {
        if (!this.hasProfileDropdownTarget) {
            return
        }

        const dropdown = this.profileDropdownTarget

        if (dropdown.classList.contains('hidden')) {
            return
        }

        const dropdownRect = dropdown.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const padding = 16

        if (viewportWidth < (dropdownRect.right + padding)) {
            dropdown.style.right = `0px`
        } else {
            dropdown.removeAttribute('style')
        }
    }
}
